import { z } from 'zod';

/**
 * Money and rates without floating-point arithmetic.
 *
 * - Amounts are integer **paise** (1 rupee = 100 paise) in every new financial
 *   record, request and response field named `*Paise`.
 * - Percentages and rates are integer **basis points** (1% = 100 bp, 100% = 10 000 bp).
 * - Products are computed with BigInt and rounded half away from zero, so
 *   ₹1e11 × 100% stays exact (Number would overflow 2^53 at ~₹9e13 × bp).
 */

export const MAX_PAISE = 1e15; // ₹10 trillion — far above any realistic value, below 2^53
export const BP_100 = 10_000;

/** Parse a rupee amount typed by a user ("1,20,000.50", 1200.5) into paise. Returns null if invalid. */
export function rupeesToPaise(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined || input === '') return null;
  const s = typeof input === 'number' ? (Number.isFinite(input) ? String(input) : '') : input.replace(/[,\s₹]/g, '');
  const m = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) return null;
  const paise = Number(m[2]) * 100 + Number((m[3] ?? '').padEnd(2, '0'));
  if (!Number.isSafeInteger(paise) || paise > MAX_PAISE) return null;
  return m[1] ? -paise : paise;
}

/** Paise → rupees for display only. Never use the result for further arithmetic. */
export function paiseToRupees(paise: number | null | undefined): number | null {
  return paise === null || paise === undefined ? null : paise / 100;
}

function roundDiv(num: bigint, den: bigint): bigint {
  const neg = num < 0n !== den < 0n;
  const a = num < 0n ? -num : num;
  const b = den < 0n ? -den : den;
  const q = (a * 2n + b) / (b * 2n); // half away from zero
  return neg ? -q : q;
}

/** amount × rate, where rate is in basis points (e.g. 18% GST = 1800). */
export function applyBp(paise: number, bp: number): number {
  return Number(roundDiv(BigInt(paise) * BigInt(bp), BigInt(BP_100)));
}

/** amount × quantity, where quantity is in hundredths (2.5 units = 250). */
export function multiplyQty(unitPaise: number, qtyHundredths: number): number {
  return Number(roundDiv(BigInt(unitPaise) * BigInt(qtyHundredths), 100n));
}

/** part / whole in basis points (75% = 7500). Zero when `whole` is zero. */
export function ratioBp(part: number, whole: number): number {
  if (!whole) return 0;
  return Number(roundDiv(BigInt(part) * BigInt(BP_100), BigInt(whole)));
}

/** `amount` × numerator / denominator with exact rounding (pro-rata salary, daily breakdowns). */
export function prorate(paise: number, numerator: number, denominator: number): number {
  if (!denominator) return 0;
  return Number(roundDiv(BigInt(paise) * BigInt(numerator), BigInt(denominator)));
}

/**
 * Split `total` by weights so the parts always add up to exactly `total`
 * (largest-remainder method). Used for shared revenue credit and breakdowns.
 */
export function allocate(total: number, weights: number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (!weights.length || sum <= 0) return weights.map(() => 0);
  const T = BigInt(total);
  const S = BigInt(sum);
  const exact = weights.map((w) => T * BigInt(w));
  const base = exact.map((e) => e / S);
  let rest = Number(T - base.reduce((a, b) => a + b, 0n));
  const order = exact
    .map((e, i) => ({ i, r: e % S }))
    .sort((a, b) => (a.r === b.r ? a.i - b.i : a.r > b.r ? -1 : 1));
  const out = base.map(Number);
  for (const { i } of order) {
    if (rest <= 0) break;
    out[i]! += 1;
    rest -= 1;
  }
  return out;
}

/** Format paise for documents (PDF/XLSX), e.g. "INR 1,20,000.00". */
export function formatPaise(paise: number | null | undefined, opts: { locale?: string; currency?: string; symbol?: boolean } = {}): string {
  if (paise === null || paise === undefined) return '—';
  const locale = opts.locale ?? 'en-IN';
  const currency = opts.currency ?? 'INR';
  const neg = paise < 0;
  const abs = Math.abs(paise);
  const whole = Math.floor(abs / 100);
  const frac = String(abs % 100).padStart(2, '0');
  const grouped = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(whole);
  const body = opts.symbol === false ? `${grouped}.${frac}` : `${currency} ${grouped}.${frac}`;
  return neg ? `-${body}` : body;
}

/** Basis points → "12.5%". */
export function formatBp(bp: number | null | undefined): string {
  if (bp === null || bp === undefined) return '—';
  const pct = bp / 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}%`;
}

// ------------------------------------------------------------------ zod building blocks
export const paiseSchema = z.number().int('Amounts must be whole paise').min(0).max(MAX_PAISE);
export const signedPaiseSchema = z.number().int('Amounts must be whole paise').min(-MAX_PAISE).max(MAX_PAISE);
export const positivePaiseSchema = z.number().int('Amounts must be whole paise').min(1, 'Amount must be greater than zero').max(MAX_PAISE);
/** 0 – 100% in basis points. */
export const bpSchema = z.number().int().min(0).max(BP_100);
/** A month such as 2026-11. */
export const monthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM');
export const dateKeySchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Use YYYY-MM-DD');

/** Months between two YYYY-MM keys (inclusive start, exclusive end). */
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const idx = y * 12 + (m - 1) + n;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

export function compareMonths(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
